import {
  CTCSS_TONES,
  DTCS_CODES,
  decodeLbcd,
  encodeLbcd,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x41
const BLOCK_SIZE = 0x80
const MEMORY_SIZE = 0xff10
const RADIO_START = 0x0100
const RADIO_READ_END = 0x10000
const RADIO_WRITE_END = 0xf400
const CHANNEL_SIZE = 32
const CHANNEL_COUNT = 800
const CHANNEL_START = 0x0010
const ACTIVE_START = 0xcb90
const SCAN_START = ACTIVE_START + 128
const PRIORITY_START = SCAN_START + 128
const MODES = ['WFM', 'FM', 'NFM']
const POWER_LEVELS = ['Low', 'Mid2', 'Mid1', 'High']
const STEPS = [2.5, 5, 6.25, 7.5, 8.33, 10, 12.5, 15, 20, 25, 30, 50, 100]

export type TH7800Profile = {
  protocol: 'tyt-th7800'
  id: string
  vendor: string
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 38400
  channelMinimum: 1
  channelMaximum: number
  nameLength: 6
  memorySize: number
  powerLevels: readonly string[]
  modes: readonly string[]
  handshake: 'th7800' | 'th9800'
  channelFormat: 'standard' | 'ma1'
}

export const TH7800_PROFILES: readonly TH7800Profile[] = [
  {
    protocol: 'tyt-th7800',
    id: 'tyt-th-7800',
    vendor: 'TYT',
    model: 'TH-7800',
    summary: 'TYT TH-7800 dual-band/quad-display mobile.',
    tags: ['Experimental', 'Read + write', '800 channels', '6-character names'],
    baudRate: 38400,
    channelMinimum: 1,
    channelMaximum: CHANNEL_COUNT,
    nameLength: 6,
    memorySize: MEMORY_SIZE,
    powerLevels: POWER_LEVELS,
    modes: [...MODES, 'AM'],
    handshake: 'th7800',
    channelFormat: 'standard',
  },
  {
    protocol: 'tyt-th7800',
    id: 'tyt-th-9800',
    vendor: 'TYT',
    model: 'TH-9800',
    summary: 'TYT TH-9800 quad-band mobile.',
    tags: ['Experimental', 'Read + write', '800 channels', '6-character names'],
    baudRate: 38400,
    channelMinimum: 1,
    channelMaximum: CHANNEL_COUNT,
    nameLength: 6,
    memorySize: MEMORY_SIZE,
    powerLevels: POWER_LEVELS,
    modes: [...MODES, 'AM'],
    handshake: 'th9800',
    channelFormat: 'standard',
  },
  {
    protocol: 'tyt-th7800',
    id: 'retevis-ma1',
    vendor: 'Retevis',
    model: 'MA1',
    summary: 'Retevis MA1 mobile radio.',
    tags: ['Experimental', 'Read + write', '999 channels', '6-character names'],
    baudRate: 38400,
    channelMinimum: 1,
    channelMaximum: 999,
    nameLength: 6,
    memorySize: MEMORY_SIZE,
    powerLevels: ['Low', 'Mid', 'High'],
    modes: ['FM', 'NFM', 'AM'],
    handshake: 'th9800',
    channelFormat: 'ma1',
  },
]

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'FM',
    power: 'Low',
    skip: false,
  }
}

function bitmap(image: Uint8Array, start: number, number: number): boolean {
  return (
    image[start + Math.floor((number - 1) / 8)] &
    (1 << ((number - 1) % 8))
  ) !== 0
}

function setBitmap(
  image: Uint8Array,
  start: number,
  number: number,
  enabled: boolean,
): void {
  const offset = start + Math.floor((number - 1) / 8)
  const bit = 1 << ((number - 1) % 8)
  image[offset] = enabled
    ? image[offset] | bit
    : image[offset] & ~bit
}

function decodeName(raw: Uint8Array): string {
  return String.fromCharCode(
    ...raw.map((value) =>
      value === 0xff || value === 0 ? 0x20 : value,
    ),
  )
    .replace(/[\u0000\ufffd]/g, '')
    .trim()
}

function encodeName(value: string): Uint8Array {
  const result = new Uint8Array(6).fill(0xff)
  const name = value
    .toUpperCase()
    .replace(/[^A-Z0-9#*+\- ]/g, '')
    .slice(0, 6)
  for (let index = 0; index < name.length; index += 1) {
    result[index] = name.charCodeAt(index)
  }
  return result
}

export function parseTH7800Channel(
  image: Uint8Array,
  profile: TH7800Profile,
  number: number,
): WebChannel {
  if (!bitmap(image, ACTIVE_START, number)) return emptyChannel(number)
  const start = CHANNEL_START + (number - 1) * CHANNEL_SIZE
  const raw = image.slice(start, start + CHANNEL_SIZE)
  if (raw.byteLength !== CHANNEL_SIZE) return emptyChannel(number)
  const rxFrequency = decodeLbcd(raw.slice(0, 4))
  if (!rxFrequency) return emptyChannel(number)
  if (profile.channelFormat === 'ma1') {
    const txFrequency = decodeLbcd(raw.slice(4, 8))
    const decodeMode = (
      mode: number,
      tone: number | null,
    ): Pick<WebChannel, 'toneMode' | 'ctcss' | 'dtcs' | 'dtcsPolarity'> => {
      if (mode === 1 && tone) {
        return {
          toneMode: 'ctcss',
          ctcss: tone / 10,
          dtcs: null,
          dtcsPolarity: 'N',
        }
      }
      if (mode >= 2 && tone) {
        return {
          toneMode: 'dtcs',
          ctcss: null,
          dtcs: tone,
          dtcsPolarity: mode === 3 ? 'R' : 'N',
        }
      }
      return {
        toneMode: 'none',
        ctcss: null,
        dtcs: null,
        dtcsPolarity: 'N',
      }
    }
    const txMode = (raw[15] >> 6) & 0x03
    const rxMode = (raw[15] >> 2) & 0x03
    const txTone = decodeMode(txMode, decodeLbcd(raw.slice(10, 12)))
    const rxTone = decodeMode(rxMode, decodeLbcd(raw.slice(8, 10)))
    const selected = txTone.toneMode === 'none' ? rxTone : txTone
    const powerIndex = (raw[12] >> 6) & 0x03
    return {
      number,
      empty: false,
      name: decodeName(raw.slice(24, 30)),
      rxFrequency,
      txFrequency,
      ...selected,
      mode: raw[20] & 0x10
        ? 'AM'
        : ['FM', 'FM', 'NFM', 'NFM'][(raw[13] >> 6) & 0x03] ?? 'FM',
      power: profile.powerLevels[powerIndex === 3 ? 2 : powerIndex] ?? 'Low',
      skip: !bitmap(image, SCAN_START, number),
      tuningStep: STEPS[raw[14] & 0x0f] ?? 5,
    }
  }
  const txValue = decodeLbcd(raw.slice(4, 8))
  const txFrequency = txValue ? txValue : null
  const ctcss = decodeLbcd(raw.slice(8, 10))
  const dtcs = decodeLbcd(raw.slice(10, 12))
  const toneMode = ['none', 'ctcss', 'ctcss', 'dtcs'][
    raw[15] & 0x03
  ] as WebChannel['toneMode']
  const polarity = (
    ['N', 'R', 'N', 'R'][(raw[15] >> 6) & 0x03] ?? 'N'
  ) as WebChannel['dtcsPolarity']
  return {
    number,
    empty: false,
    name: decodeName(raw.slice(24, 30)),
    rxFrequency,
    txFrequency,
    toneMode,
    ctcss: toneMode === 'ctcss' && ctcss ? ctcss / 10 : null,
    dtcs: toneMode === 'dtcs' && dtcs ? dtcs : null,
    dtcsPolarity: polarity,
    mode: raw[20] & 0x10 ? 'AM' : MODES[(raw[13] >> 6) & 0x03] ?? 'FM',
    power: POWER_LEVELS[(raw[12] >> 6) & 0x03] ?? 'Low',
    skip: !bitmap(image, SCAN_START, number),
    tuningStep: STEPS[raw[14] & 0x0f] ?? 5,
  }
}

export function parseTH7800Channels(
  image: Uint8Array,
  profile: TH7800Profile,
): WebChannel[] {
  return Array.from({ length: CHANNEL_COUNT }, (_, index) =>
    parseTH7800Channel(image, profile, index + 1),
  )
}

export function applyTH7800Channel(
  image: Uint8Array,
  profile: TH7800Profile,
  channel: WebChannel,
): void {
  setBitmap(image, ACTIVE_START, channel.number, !channel.empty)
  const start = CHANNEL_START + (channel.number - 1) * CHANNEL_SIZE
  const raw = image.subarray(start, start + CHANNEL_SIZE)
  if (channel.empty) {
    raw.fill(0)
    raw.fill(0xff, 0, 8)
    raw.fill(0xff, 24, 32)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  raw.fill(0)
  encodeLbcd(channel.rxFrequency, raw.subarray(0, 4))
  encodeLbcd(
    Math.round(channel.txFrequency ?? channel.rxFrequency),
    raw.subarray(4, 8),
  )
  const toneMode =
    channel.toneMode === 'dtcs'
      ? channel.dtcsPolarity === 'R'
        ? 3
        : 2
      : channel.toneMode === 'ctcss'
        ? 1
        : 0
  const toneValue =
    channel.toneMode === 'ctcss'
      ? Math.round((channel.ctcss ?? CTCSS_TONES[0]) * 10)
      : channel.toneMode === 'dtcs'
        ? channel.dtcs ?? DTCS_CODES[0]
        : 0xff
  encodeLbcd(toneValue, raw.subarray(8, 10))
  encodeLbcd(toneValue, raw.subarray(10, 12))
  const power = Math.max(0, profile.powerLevels.indexOf(channel.power))
  raw[12] =
    ((power & 0x03) << 6) |
    (channel.name ? 0x04 : 0) |
    (channel.skip ? 0 : 0x01)
  raw[13] =
    (Math.max(
      0,
      MODES.indexOf(channel.mode === 'AM' ? 'FM' : channel.mode),
    ) & 0x03) << 6
  if (channel.toneMode !== 'none') raw[13] |= 0x01
  const stepIndex = Math.max(0, STEPS.indexOf(channel.tuningStep ?? 5))
  raw[14] = stepIndex & 0x0f
  if (profile.channelFormat === 'ma1') {
    raw[15] =
      ((toneMode & 0x03) << 6) | ((toneMode & 0x03) << 2)
  } else {
    const polarity = channel.dtcsPolarity === 'R' ? 1 : 0
    raw[15] = ((polarity & 0x03) << 6) | (toneMode & 0x03)
  }
  const offset = Math.abs(
    Math.round((channel.txFrequency ?? channel.rxFrequency) - channel.rxFrequency),
  )
  encodeLbcd(offset, raw.subarray(16, 20))
  raw[20] = channel.mode === 'AM' ? 0x10 : 0
  raw.set(encodeName(channel.name), 24)
  raw[30] = 0xff
  raw[31] = 0xff
  setBitmap(image, SCAN_START, channel.number, !channel.skip)
  setBitmap(image, PRIORITY_START, channel.number, false)
}

async function enterProgrammingMode(
  profile: TH7800Profile,
  transport: RadioTransport,
): Promise<Uint8Array> {
  await transport.discardInput()
  const th7800 = profile.handshake === 'th7800'
  await transport.write(
    new TextEncoder().encode(th7800 ? '\x02SPECPR' : '\x02PROGRA'),
  )
  if ((await transport.readExact(1, 1600))[0] !== ACK) {
    throw new Error('The radio refused TH-7800 programming mode.')
  }
  await transport.write(
    new TextEncoder().encode(th7800 ? 'G\x02' : 'M\x02'),
  )
  const identity = await transport.readExact(16, 2000)
  await transport.write(Uint8Array.of(ACK))
  if ((await transport.readExact(1, 1600))[0] !== ACK) {
    throw new Error('The radio refused the TH-7800 clone session.')
  }
  return identity
}

async function exitProgrammingMode(
  transport: RadioTransport,
  command: 'ENDR' | 'ENDW',
): Promise<void> {
  await transport.write(new TextEncoder().encode(command))
}

function frame(
  command: number,
  address: number,
  size = BLOCK_SIZE,
): Uint8Array {
  return Uint8Array.of(
    command,
    (address >> 8) & 0xff,
    address & 0xff,
    size,
  )
}

async function readBlock(
  transport: RadioTransport,
  address: number,
): Promise<Uint8Array> {
  const command = frame(0x52, address)
  await transport.write(command)
  const response = await transport.readExact(BLOCK_SIZE + 4, 3000)
  if (
    response[0] !== 0x57 ||
    !command.slice(1).every((value, index) => response[index + 1] === value)
  ) {
    throw new Error(`Unexpected TH-7800 read reply at 0x${address.toString(16)}.`)
  }
  await transport.write(Uint8Array.of(ACK))
  if ((await transport.readExact(1, 1600))[0] !== ACK) {
    throw new Error(`The radio rejected block 0x${address.toString(16)}.`)
  }
  return response.slice(4)
}

export async function readTH7800RadioWithTransport(
  profile: TH7800Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  const identity = await enterProgrammingMode(profile, transport)
  try {
    const image = new Uint8Array(profile.memorySize).fill(0xff)
    image.set(identity)
    const total = (RADIO_READ_END - RADIO_START) / BLOCK_SIZE
    let completed = 0
    for (
      let address = RADIO_START;
      address < RADIO_READ_END;
      address += BLOCK_SIZE
    ) {
      image.set(await readBlock(transport, address), address - 0xf0)
      completed += 1
      onProgress?.({
        current: completed,
        total,
        label: 'Reading TYT TH-7800 memory',
      })
    }
    return { image, reportedModel: 'TH-7800' }
  } finally {
    await exitProgrammingMode(transport, 'ENDR')
  }
}

export async function writeTH7800RadioWithTransport(
  profile: TH7800Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < profile.memorySize) {
    throw new Error('The selected image is smaller than TH-7800 memory.')
  }
  const identity = await enterProgrammingMode(profile, transport)
  if (!image.slice(0, 16).every((value, index) => value === identity[index])) {
    throw new Error('The selected image identifies as a different radio.')
  }
  try {
    const total = (RADIO_WRITE_END - RADIO_START) / BLOCK_SIZE
    let completed = 0
    for (
      let address = RADIO_START;
      address < RADIO_WRITE_END;
      address += BLOCK_SIZE
    ) {
      const packet = new Uint8Array(4 + BLOCK_SIZE)
      packet.set(frame(0x57, address))
      packet.set(image.slice(address - 0xf0, address - 0xf0 + BLOCK_SIZE), 4)
      await transport.write(packet)
      if ((await transport.readExact(1, 2200))[0] !== ACK) {
        throw new Error(
          `The radio rejected TH-7800 write block 0x${address.toString(16)}.`,
        )
      }
      completed += 1
      onProgress?.({
        current: completed,
        total,
        label: 'Writing TYT TH-7800 memory',
      })
    }
  } finally {
    await exitProgrammingMode(transport, 'ENDW')
    try {
      await transport.readExact(3, 500)
    } catch {
      // Some firmware revisions omit the final status bytes.
    }
  }
}
